import { useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import useSolarProposalProductOffering from "~/api/hooks/useSolarProposalProductOffering"

import {
  isSolarUpdateRunnable,
  SOLAR_UPDATES_SPEC,
  solarLightReachPricingFix,
  solarUpdatesNeeded,
  solarUpdateStaleLabel,
  type SolarStaleFigures,
  type SolarUpdateKey,
  type SolarUpdateReads,
  toSolarStaleFigures,
} from "./solar-updates"
import { useSolarUpdatesRunner } from "./use-solar-updates-runner"
import { updatesBlockedBy } from "../ProposalPresentation/updates/proposal-updates"
import { UpdatesPanel } from "../ProposalPresentation/updates/UpdatesPanel"
import {
  type UpdatesRailState,
  useUpdatesPanelState,
} from "../ProposalPresentation/updates/use-updates-panel-state"

export const SOLAR_UPDATES_INTRO = `Your edits changed this proposal. Update it before you present or share.`
export const SOLAR_UPDATES_LOCKED_FALLBACK = `Financing locks this proposal's selections.`
export const SOLAR_UPDATES_LOCK_UNAVAILABLE = `Couldn't check whether financing locks this proposal.`

/**
 * Updates a financing lock alone doesn't block: savings recompute from the
 * locked selections and change none of them. An approval can move the payment
 * the savings read, and the contract waits on current savings. A sent Vision
 * contract still freezes them (`savingsLocked`).
 */
const UNLOCKED_UPDATES: ReadonlySet<SolarUpdateKey> = new Set([`savings`])

/** What the section rail shows while the panel is up or minimized. */
export type SolarUpdatesRailState = UpdatesRailState

export type SolarUpdates = {
  /** Updates needed, in fix order. */
  readonly needed: readonly SolarUpdateKey[]
  readonly staleFigures: SolarStaleFigures
  readonly rail: SolarUpdatesRailState
  /** The panel and its minimized tab, for the presentation's chrome. */
  readonly panel: JSX.Element | null
}

const NO_UPDATES: readonly SolarUpdateKey[] = []

/**
 * The seller's Updates panel: it slides in while pricing, savings or
 * production is out of date and slides away once everything is current. The
 * document stays clean; the panel owns every fix. `enabled` is false for a
 * read-only viewer, who gets no panel, marks or badges at all.
 */
export const useSolarUpdates = ({
  proposalId,
  reads,
  enabled,
}: {
  proposalId: string
  reads: SolarUpdateReads
  enabled: boolean
}): SolarUpdates => {
  const needed = enabled ? solarUpdatesNeeded(reads) : NO_UPDATES
  const runner = useSolarUpdatesRunner({
    proposalId,
    settled: needed.length === 0,
    priceSetByOffer: reads.priceSetByOffer,
  })
  const panel = useUpdatesPanelState({
    spec: SOLAR_UPDATES_SPEC,
    needed,
    runner,
    enabled,
  })

  // The lock read is needed only while there's something to fix. A cached
  // read may predate a lock, so activating the panel always checks again, and
  // the actions wait for that answer.
  const lockQuery = useSolarProposalProductOffering(
    panel.active ? proposalId : undefined,
    { staleTime: 0 },
  )
  const lock = lockQuery.data?.financingLocked
    ? (lockQuery.data.financingLockReason ?? SOLAR_UPDATES_LOCKED_FALLBACK)
    : null
  const lockPending =
    panel.active &&
    (lockQuery.isFetching || (lockQuery.data == null && !lockQuery.isError))
  // An older read without the field keeps savings under the financing lock.
  const savingsLocked = lockQuery.data?.savingsLocked ?? lock !== null
  // An unknown lock keeps every action disabled: these commands don't check
  // the lock themselves. A failed check counts even over a cached answer.
  const lockUnavailable = panel.active && lockQuery.isError
  const lockBlocks = lock !== null || lockPending || lockUnavailable
  // Under the lock an unlocked update runs alone: an update it reads that is
  // still out of date can't run first, so it waits for that.
  const locked = (key: SolarUpdateKey): boolean =>
    UNLOCKED_UPDATES.has(key)
      ? lockPending ||
        lockUnavailable ||
        savingsLocked ||
        (lockBlocks &&
          SOLAR_UPDATES_SPEC.dependencies[key].some((dependency) =>
            needed.includes(dependency),
          ))
      : lockBlocks
  // A fix builds on its dependencies' reads; while one of those can't say
  // whether it's current, the fix waits.
  const unknown = panel.active
    ? updatesBlockedBy({
        spec: SOLAR_UPDATES_SPEC,
        key: `all`,
        unknown: reads.unknown,
      })
    : []
  const queryClient = useQueryClient()
  const readKeys: Record<SolarUpdateKey, readonly unknown[]> = {
    production: queryKeys.solarProposal.design(proposalId),
    pricing: queryKeys.solarProposal.pricing(proposalId),
    savings: queryKeys.solarProposal.savings(proposalId),
  }

  return {
    needed,
    staleFigures: toSolarStaleFigures(needed),
    rail: panel.rail,
    panel: enabled ? (
      <UpdatesPanel
        spec={SOLAR_UPDATES_SPEC}
        intro={SOLAR_UPDATES_INTRO}
        open={panel.open}
        minimized={panel.minimized}
        rows={panel.rows}
        needed={needed}
        runner={runner}
        staleLabel={(key) => solarUpdateStaleLabel(key, reads)}
        // A LightReach plan's price is chosen with its offer, on Loan Product.
        fixElsewhere={(key) =>
          isSolarUpdateRunnable(key, reads)
            ? null
            : solarLightReachPricingFix(proposalId)
        }
        locked={locked}
        lock={lock}
        lockUnavailable={lockUnavailable ? SOLAR_UPDATES_LOCK_UNAVAILABLE : null}
        onRetryLock={() => void lockQuery.refetch()}
        unknown={unknown}
        onRetryUnknown={() => {
          for (const key of unknown) {
            void queryClient.refetchQueries({
              queryKey: readKeys[key],
              exact: true,
            })
          }
        }}
        onMinimize={panel.onMinimize}
        onRestore={panel.onRestore}
      />
    ) : null,
  }
}
